import React from 'react';
import { isPatient } from 'utils/userTypeHelper';
import PropTypes from 'prop-types';
import { Table } from 'reactstrap';
import EditButton from 'components/atoms/editButton';
import useUser from 'utils/useUser';
import { useSelector } from 'react-redux';

const PatientThresholds = ({ thresholds, handleEditClick }) => {
  const { userType } = useUser();
  const patientDevices = useSelector((state) => state.patient.devices);
  const isBloodGlucoseType = patientDevices.some((device) =>
    device.readingTypes.some((type) => type === 'BloodGlucose'),
  );
  const isWeightType = patientDevices.some((device) =>
    device.readingTypes.some((type) => type === 'Weight'),
  );
  const isSPO2Type = patientDevices.some((device) =>
    device.readingTypes.some((type) => type === 'SpO2'),
  );
  const isBloodPressureType = patientDevices.some((device) =>
    device.readingTypes.some((type) => type === 'BloodPressure'),
  );
  const isSleepMatType = patientDevices.some((device) =>
    device.readingTypes.some(
      (type) =>
        type === 'SleepingMatDuration' || type === 'SleepingMatActivity',
    ),
  );
  const isPeakFlowType = patientDevices.some((device) =>
    device.readingTypes.some((type) => type === 'PeakFlow'),
  );

  const isThresholdDevice =
    isBloodGlucoseType ||
    isWeightType ||
    isSPO2Type ||
    isBloodPressureType ||
    isSleepMatType;

  return (
    isThresholdDevice && (
      <Table borderless border="0" cellSpacing="0" cellPadding="0">
        <thead>
          <tr>
            <th>Reading Type</th>
            <th>
              <div className="pagination-container">
                <h5>Thresholds</h5>
                {!isPatient(userType) && (
                  <EditButton handleClick={handleEditClick} />
                )}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {isBloodPressureType && (
            <>
              <tr>
                <td>Systolic</td>
                <td>
                  <span>Max Critical: {thresholds.maxCriticalSystolic}</span>
                  <span>Min Critical: {thresholds.minCriticalSystolic}</span>
                  <span>Max At Risk: {thresholds.maxRiskSystolic}</span>
                  <span>Min At Risk: {thresholds.minRiskSystolic}</span>
                </td>
              </tr>
              <tr>
                <td>Diastolic</td>
                <td>
                  <span>Max Critical: {thresholds.maxCriticalDiastolic}</span>
                  <span>Min Critical: {thresholds.minCriticalDiastolic}</span>
                  <span>Max At Risk: {thresholds.maxRiskDiastolic}</span>
                  <span>Min At Risk: {thresholds.minRiskDiastolic}</span>
                </td>
              </tr>
            </>
          )}
          {(isBloodPressureType || isSPO2Type) && (
            <tr>
              <td>Pulse</td>
              <td>
                <span>Max Critical: {thresholds.maxCriticalPulse}</span>
                <span>Min Critical: {thresholds.minCriticalPulse}</span>
                <span>Max At Risk: {thresholds.maxRiskPulse}</span>
                <span>Min At Risk: {thresholds.minRiskPulse}</span>
              </td>
            </tr>
          )}
          {isSleepMatType && (
            <tr>
              <td>Resting Pulse</td>
              <td>
                <span>Max Critical: {thresholds.maxCriticalSleepPulse}</span>
                <span>Min Critical: {thresholds.minCriticalSleepPulse}</span>
                <span>Max At Risk: {thresholds.maxRiskSleepPulse}</span>
                <span>Min At Risk: {thresholds.minRiskSleepPulse}</span>
              </td>
            </tr>
          )}
          {isSPO2Type && (
            <tr>
              <td>Sp02</td>
              <td>
                <span>Max Critical: {thresholds.criticalBloodoxygen}</span>
                <span>Max At Risk: {thresholds.riskBloodoxygen}</span>
              </td>
            </tr>
          )}
          {isBloodGlucoseType && (
            <tr>
              <td>Glucose</td>
              <td>
                <span>Max Critical: {thresholds.maxCriticalGlucose}</span>
                <span>Min Critical: {thresholds.minCriticalGlucose}</span>
                <span>Max At Risk: {thresholds.maxRiskGlucose}</span>
                <span>Min At Risk: {thresholds.minRiskGlucose}</span>
              </td>
            </tr>
          )}
          {isWeightType && (
            <tr>
              <td>Weight</td>
              <td>
                <span>Base Line: {thresholds.baseLineWeight}</span>
                <span>Max Critical: {thresholds.maxCriticalWeight}</span>
                <span>Min Critical: {thresholds.minCriticalWeight}</span>
                <span>Max At Risk: {thresholds.maxRiskWeight}</span>
                <span>Min At Risk: {thresholds.minRiskWeight}</span>
              </td>
            </tr>
          )}
          {isPeakFlowType && (
            <>
              <tr>
                <td>PEF</td>
                <td>
                  <span>Min Critical: {thresholds?.minCriticalPef}</span>
                  <span>Min At Risk: {thresholds?.minRiskPef}</span>
                </td>
              </tr>
              <tr>
                <td>FEV1</td>
                <td>
                  <span>Min Critical: {thresholds?.minCriticalFev1}</span>
                </td>
              </tr>
            </>
          )}
        </tbody>
      </Table>
    )
  );
};

PatientThresholds.propTypes = {
  thresholds: PropTypes.shape(),
  handleEditClick: PropTypes.func.isRequired,
};

export default PatientThresholds;
